import { Grid, Stack, Typography } from '@mui/material';

import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { documentMulti } from 'common/assets/images';
import AccordionComp from 'common/components/Accordion/AccordionComp';
import AIFeedback from 'common/components/AIFeedback/AIFeedback';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Loader } from 'common/components/Loader/Loader';
import ModalEle from 'common/components/Modal/ModalEle';
import PromptSelect from 'common/components/PromptSelect/PromptSelect';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import { FEEDBACK_OBJECT_TYPE, MODULES } from 'common/constants';
import {
	AI_SIMULATION_GENAI_DISCLAIMER,
	AI_SIMULATION_MODEL_KEYS,
} from 'common/constants/AISimulationConstants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	APP_GENERAL_ERROR_MSG,
	APP_GENERIC_BAD_REQUEST_MSG,
	APP_GENERIC_DATA_NOT_FOUND_MSG,
} from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { getSelectedDocument } from '../api';
import { IObligationPolicyInputProps, IPolicyDocument } from '../api/type-def';

import PolicySelectDocumentModal from './PolicySelectDocumentModal';

import styles from './ObligationPolicyInput.module.scss';

const ObligationPolicyInput = (props: IObligationPolicyInputProps) => {
	const {
		citationInput,
		setCitationInput,
		obligation,
		setObligation,
		genObligation,
		genPromptObligation,
		genAnalysis,
		genPromptAnalysis,
		isLoadingResult,
		isLoading,
		selectedDocument,
		onSelectedDocumentChange,
		isShared,
	} = props;

	const dispatch = useDispatch();
	const [isLoadingDocument, setIsLoadingDocument] = useState<boolean>(false);
	const [isInputOpen, setIsInputOpen] = useState<boolean>(!obligation);
	const [isObliOpen, setIsObliOpen] = useState<boolean>(true);
	const [isDocOpen, setIsDocOpen] = useState<boolean>(true);
	const [showDocumentModal, setShowDocumentModal] = useState<boolean>(false);
	const [checkBoxInfo, setCheckBoxInfo] = useState<IPolicyDocument[]>([]);
	const { DISCLAIMER } = AI_SIMULATION_GENAI_DISCLAIMER;
	const { OK, BAD_REQUEST, NOT_FOUND } = HTTP_STATUS_CODES;
	const { AI_SIMULATION_AND_EXPERIMENTATION } = MODULES;
	const { OBLIGATION_TO_POLICY_KEY_OBLIGATION } = FEEDBACK_OBJECT_TYPE;
	const { OBLIGATION_TO_POLICY_KEY, OBLIGATION_GENERATION_KEY } =
		AI_SIMULATION_MODEL_KEYS;

	const selectedAIRecipe = useSelector(
		(state: RootState) => state.simulationArena.selectedAIRecipe,
	);

	useEffect(() => {
		if (!isLoading && obligation) {
			setIsInputOpen(false);
		}
	}, [isLoading]);

	const fetchSelectedDocuments = async () => {
		setIsLoadingDocument(true);

		const responseFromSelectApi = await getSelectedDocument();
		if (
			responseFromSelectApi?.responseBody &&
			responseFromSelectApi?.statusCode === OK
		) {
			setCheckBoxInfo(responseFromSelectApi?.responseBody.files);
			setIsLoadingDocument(false);
		} else {
			const { errorBody } = responseFromSelectApi;
			const errorApiMsg = errorBody?.errorMessage as string;
			if (responseFromSelectApi?.statusCode === BAD_REQUEST) {
				dispatch(
					setToastMessage({
						message: errorApiMsg || APP_GENERIC_BAD_REQUEST_MSG,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			} else if (responseFromSelectApi?.statusCode === NOT_FOUND) {
				dispatch(
					setToastMessage({
						message: errorApiMsg || APP_GENERIC_DATA_NOT_FOUND_MSG,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			} else {
				dispatch(
					setToastMessage({
						message: errorApiMsg || APP_GENERAL_ERROR_MSG,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			}
			setIsLoadingDocument(false);
		}
	};

	const handleSelectDocument = () => {
		setShowDocumentModal(true);
		fetchSelectedDocuments();
	};

	const handleModalCancelClick = () => {
		setShowDocumentModal(false);
	};
	const handleModalSubmitClick = () => {
		setShowDocumentModal(false);
	};

	let docDisplayName = 'No documents selected';
	if (selectedDocument.length > 1) {
		docDisplayName = `${selectedDocument.length} Documents selected`;
	} else if (selectedDocument.length === 1) {
		docDisplayName = `${selectedDocument.length} Document selected`;
	}
	return (
		<Stack sx={{ width: '100%' }}>
			<AccordionComp
				isLeftAccordian
				dataTestId="input-comp"
				className={styles['accordian-header']}
				detailsStyle={styles['accordian-detail']}
				parentDivStyle={styles['accordian-header']}
				onChange={() => {
					setIsInputOpen(!isInputOpen);
				}}
				expanded={isInputOpen}
				summary={
					<Typography data-testid="mapping-test-id">
						{toSentenceCase('Regulatory Citation Input')}
					</Typography>
				}
				details={
					<Grid
						display="flex"
						gap="10px"
						flexDirection="column"
						className={styles['obligation-generate-container']}
					>
						<TextFieldWithLabel
							inputProps={{
								'data-testid': 'input-textarea',
							}}
							id="input-textarea"
							required
							value={citationInput}
							onChange={(e) => setCitationInput(e.target.value)}
							multiLine
							showLabel={false}
							error={false}
							helperText=""
							className={styles.textarea}
							placeholder={toSentenceCase('Paste Regulatory Citation here *')}
						/>
						<PromptSelect
							aiModelKey={OBLIGATION_GENERATION_KEY}
							btnType="primary"
							onSelectPrompt={genPromptObligation}
							data-testid="gen-obli-btn"
							size="small"
							disabled={!citationInput}
							loading={isLoading}
							clickHandler={genObligation}
							compClass={styles['obligation-generate']}
							groupClass={styles['obligation-generate-group']}
						>
							{toSentenceCase('Generate Obligation')}
						</PromptSelect>
					</Grid>
				}
			/>
			{obligation && (
				<>
					<AccordionComp
						isLeftAccordian
						dataTestId="obli-output-comp"
						className={styles['accordian-header']}
						detailsStyle={styles['accordian-detail']}
						parentDivStyle={styles['accordian-header']}
						onChange={() => {
							setIsObliOpen(!isObliOpen);
						}}
						expanded={isObliOpen}
						summary={
							<Typography data-testid="obli-title">
								{toSentenceCase('Key Obligations')}
							</Typography>
						}
						details={
							<TextFieldWithLabel
								inputProps={{
									'data-testid': 'output-textarea',
								}}
								id="output-textarea"
								value={obligation}
								onChange={(e) => setObligation(e.target.value)}
								multiLine
								showLabel={false}
								error={false}
								helperText=""
								className={styles.textarea}
							/>
						}
					/>
					<Grid display="flex" gap="10px" flexDirection="column">
						<Typography className={styles['disclaimer-text']}>
							{DISCLAIMER}
							<AIFeedback
								objectType={OBLIGATION_TO_POLICY_KEY_OBLIGATION}
								module={AI_SIMULATION_AND_EXPERIMENTATION}
								objectData={{
									prompt: citationInput,
									response: obligation,
									reference: selectedAIRecipe?.id || '',
								}}
								shouldResetFeedback={isLoading}
								allowSaveObjectType
							/>
						</Typography>
						<AccordionComp
							isLeftAccordian
							dataTestId="doc-comp"
							className={styles['accordian-header']}
							parentDivStyle={styles['accordian-header']}
							onChange={() => {
								setIsDocOpen(!isDocOpen);
							}}
							expanded={isDocOpen}
							summary={
								<Typography data-testid="doc-title">
									{toSentenceCase('Policy/Procedure Documents')}
								</Typography>
							}
							details={
								<Grid container justifyContent="space-between" display="flex">
									<Grid item>
										<Stack direction="row" className={styles['icon-text']}>
											<img
												src={documentMulti}
												alt="doc"
												className={styles['document-icon']}
											/>
											<Typography className={styles['covered-header']}>
												{docDisplayName}
											</Typography>
										</Stack>
									</Grid>

									<ButtonComp
										data-testid="select-document"
										btnType="secondary"
										disabled={isLoadingResult}
										clickHandler={handleSelectDocument}
									>
										{selectedDocument.length > 0
											? toSentenceCase('Edit Documents')
											: toSentenceCase('Select Documents')}
									</ButtonComp>
								</Grid>
							}
						/>
					</Grid>
					<Grid className={styles['accordian-detail']} mb={1}>
						<PromptSelect
							aiModelKey={OBLIGATION_TO_POLICY_KEY}
							btnType="primary"
							size="small"
							clickHandler={genAnalysis}
							onSelectPrompt={genPromptAnalysis}
							data-testid="generate-analysis"
							loading={isLoadingResult}
							disabled={selectedDocument.length === 0 || isShared}
							compClass={styles['obligation-generate']}
							groupClass={styles['obligation-generate-group']}
						>
							{toSentenceCase('Generate Regulation Coverage Analysis')}
						</PromptSelect>
					</Grid>
				</>
			)}
			<ModalEle
				open={showDocumentModal}
				sxBoxProps={{ width: '60%', height: '60%', margin: 'auto' }}
			>
				{isLoadingDocument ? (
					<Loader show={isLoadingDocument} />
				) : (
					<PolicySelectDocumentModal
						checkBoxInfo={checkBoxInfo || []}
						selectedFiles={selectedDocument || []}
						cancelClicked={handleModalCancelClick}
						submitClicked={(selectedDocuments: string[]) => {
							onSelectedDocumentChange(selectedDocuments);
							handleModalSubmitClick();
						}}
					/>
				)}
			</ModalEle>
		</Stack>
	);
};

export default ObligationPolicyInput;
